<template>
  <div class="field-mapping-builder">
    <b-tabs content-class="mt-3">
      <b-tab v-for="(sheet, sheetName) in sheets" :key="sheetName" :title="capitalizeFirst(sheetName)" class="sheet-mapping mb-4">
        <div class="row">
          <!-- File Fields (Left Column) -->
          <div class="col-md-3">
            <div class="sticky-column">
              <h6>{{$t('import_entities.file_fields')}}</h6>
              <draggable
                :list="sheet.fields"
                :group="{ name: 'fields', pull: 'clone', put: false }"
                class="field-list"
                ghost-class="ghost"
                :clone="cloneField"
              >
                <div
                  v-for="field in sheet.fields"
                  :key="field"
                  class="field-item"
                  :title="field"
                >
                  {{ field }}
                </div>
              </draggable>
            </div>
          </div>

          <!-- Model Fields (Right Column) -->
          <div class="col-md-9">
            <h6>{{ $t('import_entities.model_fields') }}</h6>
            <div v-if="!modelFields[sheetName]" class="alert alert-warning">
              {{ $t('import_entities.no_model_available') }} {{ sheetName }}
            </div>
            <template v-else>
              <div class="model-fields-list">
                <div v-for="field in modelFields[sheetName]" :key="field.name" class="model-field-container">
                  <div class="model-field-header">
                    <span class="model-field-name">{{ field.label }}</span>
                    <span v-if="field.json_field" class="model-field-category">({{ field.json_field }})</span>
                    <span v-if="field.required" class="required-badge">*</span>
                    <span v-if="canHaveMultipleMappings(field)" class="multiple-badge" :title="field.type">
                      📎
                    </span>
                  </div>

                  <draggable
                    :list="getFieldMappings(sheetName, field.name)"
                    :group="{ name: 'fields' }"
                    class="field-mappings-list"
                    ghost-class="ghost"
                    @change="(e) => onMappingChange(sheetName, field.name, e)"
                  >
                    <div
                      v-for="(mapping, index) in getFieldMappings(sheetName, field.name)"
                      :key="`${mapping.source}_${index}`"
                      class="field-item mapped"
                    >
                      <div class="mapping-content">
                        <span class="mapping-text">{{ mapping.source }}</span>
                        <div class="mapping-controls">
<!--                          <b-form-checkbox-->
<!--                            v-if="canBeUnique(field)"-->
<!--                            v-model="mapping.unique"-->
<!--                            title="Unique field"-->
<!--                            class="unique-checkbox"-->
<!--                            @change="emitChange"-->
<!--                          />-->
                          <b-button
                            size="sm"
                            variant="danger"
                            class="delete-btn"
                            @click="removeMapping(sheetName, field.name, mapping)"
                          >
                            ×
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </draggable>
                </div>
              </div>
            </template>
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'FieldMappingBuilder',
  components: {
    draggable,
  },
  props: {
    sheets: {
      type: Object,
      default: () => ({}),
    },
    modelFields: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      mappings: { ...this.value },
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.mappings = { ...newVal }
      },
      deep: true,
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    cloneField(field) {
      return {
        source: field,
        unique: false,
      }
    },
    capitalizeFirst(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    getFieldMappings(sheetName, fieldName) {
      if (!this.mappings[sheetName]) {
        this.$set(this.mappings, sheetName, {})
      }
      if (!this.mappings[sheetName][fieldName]) {
        this.$set(this.mappings[sheetName], fieldName, [])
      }
      return this.mappings[sheetName][fieldName]
    },
    onMappingChange(sheetName, fieldName, event) {
      if (event.added) {
        const mapping = event.added.element
        if (!this.mappings[sheetName]) {
          this.$set(this.mappings, sheetName, {})
        }
        if (!this.mappings[sheetName][fieldName]) {
          this.$set(this.mappings[sheetName], fieldName, [])
        }

        const existingMapping = this.mappings[sheetName][fieldName].find(m => m.source === mapping.source)
        if (!existingMapping) {
          const fieldDef = this.modelFields[sheetName].find(f => f.name === fieldName)
          if (!this.canHaveMultipleMappings(fieldDef) && this.mappings[sheetName][fieldName].length > 0) {
            this.mappings[sheetName][fieldName] = [mapping]
          } else {
            this.mappings[sheetName][fieldName].push(mapping)
          }
        }

        this.emitChange()
      }
    },
    canHaveMultipleMappings(fieldDef) {
      if (!fieldDef) return true

      const multipleFields = ['route', 'address', 'full_name', 'description']
      const allowMultipleTypes = ['text', 'textarea']

      return multipleFields.includes(fieldDef.name) ||
             allowMultipleTypes.includes(fieldDef.type) ||
             !fieldDef.required
    },
    canBeUnique(fieldDef) {
      if (!fieldDef) return false

      const uniqueableTypes = ['text', 'number', 'email']
      return uniqueableTypes.includes(fieldDef.type)
    },
    removeMapping(sheetName, fieldName, mapping) {
      const mappings = this.mappings[sheetName][fieldName]
      const index = mappings.findIndex(m =>
        m.source === mapping.source &&
        m.unique === mapping.unique
      )
      if (index > -1) {
        mappings.splice(index, 1)
        this.emitChange()
      }
    },
    emitChange() {
      this.$emit('input', this.mappings)
    },
    handleScroll() {
      const stickyCols = this.$el.querySelectorAll('.b-tab.active .sticky-column, .tab-pane.active .sticky-column')
      if (!stickyCols) return

      stickyCols.forEach(col => {
        const parentRect = this.$el.getBoundingClientRect()
        const topOffset = 30
        const shouldStick = parentRect.top + 60 < topOffset && parentRect.bottom > col.offsetHeight + topOffset

        if (shouldStick) {
          col.style.position = 'fixed'
          col.style.top = `${topOffset}px`
          col.style.width = `${col.parentElement.offsetWidth - 20}px`
        } else {
          col.style.position = ''
          col.style.top = ''
          col.style.width = ''
        }
      })
    },
  },
}
</script>

<style scoped>
.field-mapping-builder {
  max-width: 100%;
}

.field-list {
  min-height: 200px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
  background: #f8f9fa;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.field-mapping-builder >>> .tab-content {
  overflow: visible !important;
}

.field-item {
  padding: 6px 10px;
  margin-bottom: 5px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: move;
  font-size: 13px;
}

.field-item.mapped {
  background: #e3f2fd;
  border-color: #2196f3;
  margin-bottom: 3px;
  margin-right: 5px;
  display: inline-flex;
  align-items: center;
}

.ghost {
  opacity: 0.5;
  background: #f0f0f0;
}

.model-fields-list {
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 15px;
}

.model-field-container {
  border: 1px solid #ddd;
  border-radius: 4px;
}

.model-field-header {
  padding: 6px 10px;
  background: #f8f9fa;
  border-bottom: 1px solid #ddd;
  border-radius: 4px 4px 0 0;
  font-size: 13px;
}

.model-field-name {
  font-weight: bold;
}

.model-field-category {
  color: #666;
  margin-left: 5px;
}

.required-badge {
  color: red;
  margin-left: 5px;
  font-weight: bold;
}

.multiple-badge {
  margin-left: 5px;
  font-size: 12px;
}

.field-mappings-list {
  min-height: 40px;
  padding: 8px;
  background: #fff;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
}

.mapping-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.mapping-text {
  flex: 1;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mapping-controls {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.unique-checkbox {
  margin-right: 4px;
  margin-bottom: 0 !important;
}

.unique-checkbox .custom-control-input ~ .custom-control-label {
  font-size: 11px;
  line-height: 1;
  padding-top: 0;
}

.delete-btn {
  width: 20px;
  height: 20px;
  padding: 0;
  font-size: 12px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
}
</style>
